/**
 * Canvas side of a full frame load: rebuild every node from its loaded
 * placement, keep a running focus presentation (and the temporary
 * conversation frames it shows) when it survives the reload.
 */
import type { Node } from "@xyflow/react";
import type { FrameData } from "@/lib/frame-data";
import { isTransientConversationFrameNode } from "@/lib/transient-conversation-frame";
import type { CanvasState } from "@/stores/canvas-store";
import type { PresentationState } from "@/lib/focus/focus-types";
import { canvasFocusPresentation } from "@/lib/focus/focus-renderer-kind";
import { usePresentationStore } from "@/stores/presentation-store";
import type { FramesState } from "@/stores/frames-store";
import { useProjectStore } from "@/stores/project-store";
import {
  frameNodeFromPlacement,
  type FramePlacement,
} from "./canvas-frame-geometry";

type FrameNode = Node<FrameData>;

function currentFocusPresentation(): PresentationState | null {
  const { presentation } = usePresentationStore.getState();
  return presentation?.variant === "focus" ? presentation : null;
}

/**
 * Temporary conversation frames a reload keeps: shown by the running focus
 * presentation, in the loaded space, and only when every focused frame
 * survives (otherwise the presentation ends and they go too).
 */
export function retainedFocusFrameIds(
  state: CanvasState,
  loadedIds: ReadonlySet<string>,
  loadedSpaceId: string | undefined,
): string[] {
  const focus = currentFocusPresentation();
  if (!focus) return [];
  const previous = new Map(state.nodes.map((node) => [node.id, node]));
  const retained = focus.frameIds.filter((id) => {
    if (loadedIds.has(id)) return false;
    const node = previous.get(id);
    return (
      !!node &&
      isTransientConversationFrameNode(node) &&
      node.data.spaceId === loadedSpaceId
    );
  });
  const covered = new Set([...loadedIds, ...retained]);
  return focus.frameIds.every((id) => covered.has(id)) ? retained : [];
}

function keepPresentationGeometry(
  node: FrameNode,
  previous: FrameNode,
): FrameNode {
  return {
    ...node,
    position: previous.position,
    selected: previous.selected,
    zIndex: previous.zIndex,
    width: previous.width,
    height: previous.height,
    measured: previous.measured,
    style: {
      ...node.style,
      ...previous.style,
      width: previous.style?.width ?? previous.width ?? node.style?.width,
      height: previous.style?.height ?? previous.height ?? node.style?.height,
    },
    className: previous.className,
  };
}

/**
 * Next canvas state for a load whose frames are now in the frames store, the
 * ids that keep a node (selection keeps only those), and whether the load
 * ends the running focus presentation.
 */
export function loadedFrameNodesState(
  state: CanvasState,
  frames: Pick<FramesState, "framesById" | "frameOrder">,
  placements: ReadonlyMap<string, FramePlacement>,
): {
  canvas: Partial<CanvasState>;
  nodeIds: ReadonlySet<string>;
  endsPresentation: boolean;
} {
  const previousNodes = new Map(state.nodes.map((node) => [node.id, node]));
  const focusPresentation = currentFocusPresentation();
  const focusFrameIds = new Set(focusPresentation?.frameIds ?? []);
  const nextNodes: FrameNode[] = [];
  let loadedSpaceId: string | undefined;
  for (const id of frames.frameOrder) {
    const frame = frames.framesById[id];
    if (!frame) continue;
    const placement = placements.get(id);
    if (placement) {
      loadedSpaceId ??= frame.spaceId;
      nextNodes.push(frameNodeFromPlacement(frame, placement));
      continue;
    }
    const retained = previousNodes.get(id);
    if (retained) nextNodes.push({ ...retained, data: frame });
  }
  loadedSpaceId ??= useProjectStore.getState().currentSpace?.id;
  const nextNodeIds = new Set(nextNodes.map((node) => node.id));
  const keepsFocusPresentation =
    focusPresentation !== null &&
    focusPresentation.frameIds.every((id) => nextNodeIds.has(id));
  const focusPresentationMatchesLoadedSpace =
    focusPresentation?.frameIds.some(
      (id) => previousNodes.get(id)?.data.spaceId === loadedSpaceId,
    ) ?? false;
  const hidesAroundFocus =
    keepsFocusPresentation && !!canvasFocusPresentation(focusPresentation);
  const focusHiddenFrameIds = hidesAroundFocus
    ? new Set(
        nextNodes
          .filter((node) => !focusFrameIds.has(node.id))
          .map((node) => node.id),
      )
    : focusPresentationMatchesLoadedSpace
      ? new Set(
          state.presentationRestore?.hiddenFrameIds.filter((id) =>
            nextNodeIds.has(id),
          ) ?? [],
        )
      : new Set<string>();

  const canvas: Partial<CanvasState> = {
    // Ephemeral focus-hide never outlives the space it was created in
    focusHiddenFrameIds,
    ...(focusPresentation &&
      !keepsFocusPresentation && { presentationRestore: null }),
    nodes: nextNodes.map((node) => {
      const previous = previousNodes.get(node.id);
      const next =
        keepsFocusPresentation &&
        focusFrameIds.has(node.id) &&
        previous &&
        placements.has(node.id)
          ? keepPresentationGeometry(node, previous)
          : node;
      return {
        ...next,
        hidden:
          !!next.data.metadata?.hidden || focusHiddenFrameIds.has(node.id),
      };
    }),
  };
  return {
    canvas,
    nodeIds: nextNodeIds,
    endsPresentation: focusPresentation !== null && !keepsFocusPresentation,
  };
}
